import { createFileRoute } from '@tanstack/react-router';
import { Group, Stack, Text } from '@mantine/core';
import { motion } from 'framer-motion';
import { Activity, Bell, Database, Network, Radio, Server } from 'lucide-react';
import { Badge, Card, EmptyState, PageContainer, PageHeader, StatusDot } from '../components/ui';
import { ErrorState } from '../components/common/ErrorState';
import { LoadingState } from '../components/common/LoadingState';
import { staggerContainer, rowItem } from '../lib/motion';
import { useSystemComponents, type SystemComponent } from '../hooks/useSystemComponents';
import { useTenantAlerts } from '../hooks/useAlerts';
import { AlertFeed } from '../components/monitoring/AlertFeed';
import { Section } from '../components/ui';
import { colors } from '../theme/tokens';

export const Route = createFileRoute('/monitoring')({
  component: SystemMonitoring,
});

/**
 * Monitoring — the detection feed first, platform health second.
 *
 * This page's header has always promised "live detections and SIEM alert feed",
 * but it rendered ONLY infrastructure component rows — and for most of the
 * project's life not even those, because `/monitor/system-components` was never
 * a registered route (fixed 2026-09-13, alongside the alert feed it sits above:
 * `/monitor/tenant/alerts` was missing too, so BOTH panels on this page were
 * fed by endpoints that 404'd).
 * A SOC trainee opening Monitoring to triage found infrastructure health and no
 * detections at all — and infrastructure health already has its own page (Range
 * Health). The feed the title describes is now the primary content, with
 * component health kept underneath where it is still useful to an operator.
 */
function SystemMonitoring() {
  const { data, isPending, isError, error, refetch } = useSystemComponents();
  const alertsQ = useTenantAlerts();
  const alerts = alertsQ.data ?? [];
  const critical = alerts.filter((a) => a.severity === 'critical').length;

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Radio}
        title="Monitoring"
        description="Live detections and SIEM alert feed across your active ranges."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Monitoring' }]}
      />

      <Section
        title="Detections"
        description={
          alertsQ.isSuccess
            ? `${alerts.length} alert${alerts.length === 1 ? '' : 's'} from your ranges` +
              (critical ? ` · ${critical} critical` : '')
            : alertsQ.isError
              ? 'Alert feed unavailable.'
              : 'Connecting to the alert feed…'
        }
      >
        {alertsQ.isError ? (
          <ErrorState
            title="Could not load detections"
            message={(alertsQ.error as Error)?.message ?? 'Unknown error'}
            onRetry={() => void alertsQ.refetch()}
          />
        ) : alertsQ.isPending ? (
          <LoadingState rows={4} height={56} />
        ) : (
          <AlertFeed items={alerts} />
        )}
      </Section>

      <Section
        title="Platform components"
        description="Live state of the Forge control plane — service functions, workflows, data stores and the console gateway."
      >
      {isError ? (
        <ErrorState
          title="Could not load components"
          message={(error as Error)?.message ?? 'Unknown error'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <LoadingState rows={5} height={56} />
      ) : data && data.length === 0 ? (
        <EmptyState
          illustration={<Activity size={48} />}
          title="No components registered"
          description="Component health could not be read. The platform may still be healthy — this panel itself is what failed."
        />
      ) : (
        <motion.div variants={staggerContainer} initial="hidden" animate="show">
          <Stack gap="md">
            {data?.map((c) => (
              <motion.div key={c.name} variants={rowItem}>
                <ComponentRow component={c} />
              </motion.div>
            ))}
          </Stack>
        </motion.div>
      )}
      </Section>
    </PageContainer>
  );
}

function ComponentRow({ component: c }: { component: SystemComponent }) {
  return (
    <Card>
      <Group justify="space-between" wrap="nowrap">
        <Group gap="md" wrap="nowrap">
          <ComponentIcon name={c.name} />
          <Stack gap={2}>
            <Group gap="xs" align="center">
              <Text className="font-mono" fw={600} size="sm" c="rgb(var(--c-txt-bright))">
                {c.name}
              </Text>
              <Badge variant="neutral" size="xs">
                {c.region}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              {c.detail}
            </Text>
          </Stack>
        </Group>
        <Group gap="sm">
          <StatusDot state={c.state} size="md" />
          <Text
            size="xs"
            tt="uppercase"
            fw={700}
            c={c.state === 'running' ? colors.neon : c.state === 'warning' ? colors.hudAmber : colors.hudRed}
          >
            {c.state}
          </Text>
        </Group>
      </Group>
    </Card>
  );
}

function ComponentIcon({ name }: { name: string }) {
  const lower = name.toLowerCase();
  if (lower.includes('api')) return <Network size={16} color={colors.hudCyan} />;
  if (lower.includes('dsql') || lower.includes('aurora')) return <Database size={16} color={colors.hudMagenta} />;
  if (lower.includes('event')) return <Bell size={16} color={colors.hudAmber} />;
  if (lower.includes('dcv') || lower.includes('cognito')) return <Server size={16} color={colors.neon} />;
  return <Activity size={16} color={colors.hudCyan} />;
}
